import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import { generateUuid } from '../../../../../../../../../../utils/uuids'
import { WbsItemInfo, WbsItemLink } from '../types'

// ============================================================================
// Data Models
// ============================================================================

export interface CriticalPathLinkData extends WbsItemLink {
  fromDate: CalendarDateVO
  toDate: CalendarDateVO
  /** 編集可能かどうか。false の場合は点線で描画される */
  editable?: boolean
}

export interface CriticalPathDragState {
  isDragging: boolean
  fromRowIndex: number | null
  fromDate: CalendarDateVO | null
  hoverDate: CalendarDateVO | null
  startX: number
  startY: number
  currentX: number
  currentY: number
}

export interface RowInfo {
  isProcess: boolean
  wbsItemUuid: string | null
  projectPlanUuid?: string
}

// ============================================================================
// Drag Context (separated to prevent re-renders during D&D)
// ============================================================================

export interface CriticalPathDragContextValue {
  dragState: CriticalPathDragState
  startDrag: (params: {
    fromRowIndex: number
    fromDate: CalendarDateVO
    startX: number
    startY: number
  }) => void
  updateDragPosition: (x: number, y: number) => void
  updateHoverDate: (date: CalendarDateVO | null) => void
  endDrag: () => void
}

const defaultDragState: CriticalPathDragState = {
  isDragging: false,
  fromRowIndex: null,
  fromDate: null,
  hoverDate: null,
  startX: 0,
  startY: 0,
  currentX: 0,
  currentY: 0,
}

const CriticalPathDragContext =
  createContext<CriticalPathDragContextValue | null>(null)

const CriticalPathDragProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [dragState, setDragState] =
    useState<CriticalPathDragState>(defaultDragState)

  const startDrag = useCallback(
    (params: {
      fromRowIndex: number
      fromDate: CalendarDateVO
      startX: number
      startY: number
    }) => {
      setDragState({
        isDragging: true,
        fromRowIndex: params.fromRowIndex,
        fromDate: params.fromDate,
        hoverDate: null,
        startX: params.startX,
        startY: params.startY,
        currentX: params.startX,
        currentY: params.startY,
      })
    },
    []
  )

  const updateDragPosition = useCallback((x: number, y: number) => {
    setDragState(prev => ({
      ...prev,
      currentX: x,
      currentY: y,
    }))
  }, [])

  const updateHoverDate = useCallback((date: CalendarDateVO | null) => {
    setDragState(prev => {
      if (prev.hoverDate === date) return prev
      if (
        prev.hoverDate !== null &&
        date !== null &&
        prev.hoverDate.isEqual(date)
      ) {
        return prev
      }
      return { ...prev, hoverDate: date }
    })
  }, [])

  const endDrag = useCallback(() => {
    setDragState(defaultDragState)
  }, [])

  const contextValue = useMemo(
    () => ({
      dragState,
      startDrag,
      updateDragPosition,
      updateHoverDate,
      endDrag,
    }),
    [dragState, startDrag, updateDragPosition, updateHoverDate, endDrag]
  )

  return (
    <CriticalPathDragContext.Provider value={contextValue}>
      {children}
    </CriticalPathDragContext.Provider>
  )
}

export const useCriticalPathDragContext = (): CriticalPathDragContextValue => {
  const context = useContext(CriticalPathDragContext)
  if (!context) {
    throw new Error(
      'useCriticalPathDragContext must be used within CriticalPathDragProvider'
    )
  }
  return context
}

export const useCriticalPathDragContextOptional =
  (): CriticalPathDragContextValue | null => {
    return useContext(CriticalPathDragContext)
  }

// ============================================================================
// Critical Path Context (static data - does not change during D&D)
// ============================================================================

// Input type for adding a new critical path link (uuid is auto-generated)
export type AddCriticalPathLinkInput = Omit<
  CriticalPathLinkData,
  'uuid' | 'fromProjectPlanUuid' | 'toProjectPlanUuid'
> &
  Partial<
    Pick<
      CriticalPathLinkData,
      'uuid' | 'fromProjectPlanUuid' | 'toProjectPlanUuid'
    >
  >

// Updates for color/description of a critical path link
export type CriticalPathLinkUpdates = {
  color?: string
  description?: string
}

interface CriticalPathContextValue {
  criticalPathLinks: CriticalPathLinkData[]
  addCriticalPathLink: (link: AddCriticalPathLinkInput) => void
  removeCriticalPathLink: (linkUuid: string) => void
  updateCriticalPathLinkDate: (
    linkUuid: string,
    field: 'fromDate' | 'toDate',
    newDate: CalendarDateVO
  ) => void
  updateCriticalPathLinkProps: (
    linkUuid: string,
    updates: CriticalPathLinkUpdates
  ) => void

  rowInfo: Map<number, RowInfo>
  updateRowInfo: (rowIndex: number, info: RowInfo) => void
  // Notify that a row's cell renderer has been unmounted (e.g. row collapsed).
  // Sets affected links' rowIndex to -1 to trigger re-render and correct filtering.
  notifyRowRemoved: (wbsItemUuid: string) => void

  // Maps wbsItemUuid → current rowIndex (kept up-to-date by each row's updateRowInfo call)
  wbsItemUuidToRowIndex: Map<string, number>

  // WBS item info for resolving display names/icons of hidden items
  wbsItemInfoMap: ReadonlyMap<string, WbsItemInfo>
}

const CriticalPathContext = createContext<CriticalPathContextValue | null>(null)

export interface CriticalPathProviderProps {
  children: React.ReactNode
  criticalPaths?: CriticalPathLinkData[]
  onCriticalPathsChange?: (links: CriticalPathLinkData[]) => void
  // WBS item info map for resolving display names/icons of hidden items
  wbsItemInfoMap?: ReadonlyMap<string, WbsItemInfo>
}

const EMPTY_WBS_ITEM_INFO_MAP: ReadonlyMap<string, WbsItemInfo> = new Map()

export const CriticalPathProvider: React.FC<CriticalPathProviderProps> = ({
  children,
  criticalPaths: criticalPathsProp,
  onCriticalPathsChange,
  wbsItemInfoMap,
}) => {
  const [criticalPathLinks, setCriticalPathLinks] = useState<
    CriticalPathLinkData[]
  >(criticalPathsProp ?? [])

  const [rowInfo] = useState<Map<number, RowInfo>>(() => new Map())
  // Track all wbsItemUuid → rowIndex (like DependencyContext's wbsItemUuidToRowIndexRef)
  // to detect row index changes on expand/collapse and trigger re-renders
  const [wbsItemUuidToRowIndex] = useState<Map<string, number>>(() => new Map())

  // Sync with criticalPaths prop when it changes from parent.
  // Correct stale rowIndex values using wbsItemUuidToRowIndex Map.
  // When links arrive from fetch, their rowIndex may be stale if the grid
  // reordered rows during the async API call. The Map already has the correct
  // indices (updated by updateRowInfo before the prop sync effect fires).
  useEffect(() => {
    if (criticalPathsProp !== undefined) {
      const corrected = criticalPathsProp.map(link => {
        let updated = link
        const fromIdx = wbsItemUuidToRowIndex.get(link.fromWbsItemUuid)
        if (fromIdx !== undefined && fromIdx !== link.fromRowIndex) {
          updated = { ...updated, fromRowIndex: fromIdx }
        }
        const toIdx = wbsItemUuidToRowIndex.get(link.toWbsItemUuid)
        if (toIdx !== undefined && toIdx !== link.toRowIndex) {
          updated = { ...updated, toRowIndex: toIdx }
        }
        return updated
      })
      setCriticalPathLinks(corrected)
    }
  }, [criticalPathsProp, wbsItemUuidToRowIndex])

  // Notify parent when critical paths change
  useEffect(() => {
    if (onCriticalPathsChange) {
      onCriticalPathsChange(criticalPathLinks)
    }
  }, [criticalPathLinks, onCriticalPathsChange])

  const updateRowInfo = useCallback(
    (rowIndex: number, info: RowInfo) => {
      const existing = rowInfo.get(rowIndex)
      if (
        existing?.isProcess !== info.isProcess ||
        existing?.wbsItemUuid !== info.wbsItemUuid ||
        existing?.projectPlanUuid !== info.projectPlanUuid
      ) {
        rowInfo.set(rowIndex, info)
      }

      // Track all wbsItemUuids and detect row index changes (like DependencyContext)
      const uuid = info.wbsItemUuid
      if (!uuid) return

      const prevRowIndex = wbsItemUuidToRowIndex.get(uuid)
      wbsItemUuidToRowIndex.set(uuid, rowIndex)

      // Clean up stale rowInfo entry from previous position
      if (prevRowIndex !== undefined && prevRowIndex !== rowIndex) {
        const oldEntry = rowInfo.get(prevRowIndex)
        if (oldEntry?.wbsItemUuid === uuid) {
          rowInfo.delete(prevRowIndex)
        }
      }

      // When any row's index changes, update all links that reference it
      if (prevRowIndex !== rowIndex) {
        setCriticalPathLinks(prev => {
          let changed = false
          const next = prev.map(link => {
            let updated = link
            if (
              link.fromWbsItemUuid === uuid &&
              link.fromRowIndex !== rowIndex
            ) {
              updated = { ...updated, fromRowIndex: rowIndex }
              changed = true
            }
            if (link.toWbsItemUuid === uuid && link.toRowIndex !== rowIndex) {
              updated = { ...updated, toRowIndex: rowIndex }
              changed = true
            }
            return updated
          })
          return changed ? next : prev
        })
      }
    },
    [rowInfo, wbsItemUuidToRowIndex]
  )

  const addCriticalPathLink = useCallback(
    (link: AddCriticalPathLinkInput) => {
      setCriticalPathLinks(prev => {
        const exists = prev.some(
          l =>
            l.fromWbsItemUuid === link.fromWbsItemUuid &&
            l.toWbsItemUuid === link.toWbsItemUuid &&
            l.fromDate.isEqual(link.fromDate) &&
            l.toDate.isEqual(link.toDate)
        )
        if (exists) return prev
        return [
          ...prev,
          {
            ...link,
            uuid: link.uuid || generateUuid(),
            fromProjectPlanUuid:
              link.fromProjectPlanUuid ||
              rowInfo.get(link.fromRowIndex)?.projectPlanUuid ||
              '',
            toProjectPlanUuid:
              link.toProjectPlanUuid ||
              rowInfo.get(link.toRowIndex)?.projectPlanUuid ||
              '',
          },
        ]
      })
    },
    [rowInfo]
  )

  const removeCriticalPathLink = useCallback((linkUuid: string) => {
    setCriticalPathLinks(prev => prev.filter(l => l.uuid !== linkUuid))
  }, [])

  const updateCriticalPathLinkDate = useCallback(
    (
      linkUuid: string,
      field: 'fromDate' | 'toDate',
      newDate: CalendarDateVO
    ) => {
      setCriticalPathLinks(prev =>
        prev.map(l => (l.uuid === linkUuid ? { ...l, [field]: newDate } : l))
      )
    },
    []
  )

  const updateCriticalPathLinkProps = useCallback(
    (linkUuid: string, updates: CriticalPathLinkUpdates) => {
      setCriticalPathLinks(prev =>
        prev.map(l => (l.uuid === linkUuid ? { ...l, ...updates } : l))
      )
    },
    []
  )

  const notifyRowRemoved = useCallback(
    (wbsItemUuid: string) => {
      wbsItemUuidToRowIndex.delete(wbsItemUuid)
      // Set rowIndex to -1 for links referencing the removed row,
      // triggering a state change so all cells re-render and categorizeLinks
      // correctly filters them out or resolves fallback ancestors.
      setCriticalPathLinks(prev => {
        let changed = false
        const next = prev.map(link => {
          let updated = link
          if (
            link.fromWbsItemUuid === wbsItemUuid &&
            link.fromRowIndex !== -1
          ) {
            updated = { ...updated, fromRowIndex: -1 }
            changed = true
          }
          if (link.toWbsItemUuid === wbsItemUuid && link.toRowIndex !== -1) {
            updated = { ...updated, toRowIndex: -1 }
            changed = true
          }
          return updated
        })
        return changed ? next : prev
      })
    },
    [wbsItemUuidToRowIndex]
  )

  const contextValue = useMemo(
    () => ({
      criticalPathLinks,
      addCriticalPathLink,
      removeCriticalPathLink,
      updateCriticalPathLinkDate,
      updateCriticalPathLinkProps,
      rowInfo,
      updateRowInfo,
      notifyRowRemoved,
      wbsItemUuidToRowIndex,
      wbsItemInfoMap: wbsItemInfoMap ?? EMPTY_WBS_ITEM_INFO_MAP,
    }),
    [
      criticalPathLinks,
      addCriticalPathLink,
      removeCriticalPathLink,
      updateCriticalPathLinkDate,
      updateCriticalPathLinkProps,
      rowInfo,
      updateRowInfo,
      notifyRowRemoved,
      wbsItemUuidToRowIndex,
      wbsItemInfoMap,
    ]
  )

  return (
    <CriticalPathDragProvider>
      <CriticalPathContext.Provider value={contextValue}>
        {children}
      </CriticalPathContext.Provider>
    </CriticalPathDragProvider>
  )
}

export const useCriticalPathContext = (): CriticalPathContextValue | null => {
  return useContext(CriticalPathContext)
}
